import { useCallback, useLayoutEffect } from "react";

// Initial slider value (60 %) when text sits on a coloured/photo background — Story and post share it.
export const DEFAULT_BG_TEXT_SCALE = 0.6;

// Facebook-style cap for text posts on a coloured/photo background (Admin › Paramètres › Limites).
export const bgTextMaxChars = (config) => {
  const n = Number(config?.settings?.bg_text_max_chars);
  return Number.isFinite(n) && n > 0 ? Math.max(40, Math.min(400, Math.round(n))) : 130;
};

// Auto-fit text (bg-text posts, text stories): the largest font size in [minPx, maxPx]
// for which the REAL rendered text (line breaks, emojis, long-word wrapping) fits in
// maxW × maxH. Binary search on live layout — ~7 synchronous passes, no flicker
// (runs in useLayoutEffect). Textareas get their height set to their content too.
export function fitFontSize(el, { minPx, maxPx, maxW, maxH, isTextarea = false }) {
  // Largeur comparée à la largeur RÉELLE de l'élément (même arrondi) avec 1 px de marge : au zoom navigateur
  // (110 %, 125 %, 133 %…), scrollWidth (entier) peut dépasser d'1 px une largeur qui ne déborde pas → sinon
  // AUCUNE taille ne « tient » et le texte tombe au minimum (écrits minuscules sur fond coloré, page publique).
  const fits = (px) => {
    el.style.fontSize = `${px}px`;
    if (isTextarea) el.style.height = "0px";
    return el.scrollHeight <= maxH + 1 && el.scrollWidth <= Math.max(maxW, el.clientWidth) + 1;
  };
  let lo = minPx;
  let hi = maxPx;
  if (fits(hi)) lo = hi;
  else {
    while (hi - lo > 0.5) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) lo = mid; else hi = mid;
    }
    el.style.fontSize = `${lo}px`;
  }
  if (isTextarea) { el.style.height = "0px"; el.style.height = `${el.scrollHeight}px`; }
  return lo;
}

// `scale` (composer slider, 0.6–1.4) moves the size window (max cap + minimum) around
// the automatic one — a bigger scale can never overflow: the fit still bounds the text.
// `targetHeight`: cap for boxes that grow with their content (feed cards); fixed frames
// (stories) use the box's own inner height.
// `active`: re-arm the observer when the measured box mounts later (composer canvas).
export function useAutoFitText({ boxRef, textRef, text, scale = 1, minPx = 14, maxRatio = 0.14, maxPx = 64, targetHeight, isTextarea = false, active = true, refitKey }) {
  const refit = useCallback(() => {
    const box = boxRef.current;
    const el = textRef.current;
    if (!box || !el) return;
    const cs = getComputedStyle(box);
    const maxW = box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const boxH = box.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    const maxH = targetHeight || boxH;
    if (maxW <= 0 || maxH <= 0) return;
    const s = Math.max(0.6, Math.min(1.4, Number(scale) || 1));
    const cap = Math.max(minPx, Math.min(maxPx, maxW * maxRatio)) * s;
    fitFontSize(el, { minPx: Math.min(Math.max(10, minPx * s), cap), maxPx: cap, maxW, maxH, isTextarea });
  }, [boxRef, textRef, scale, minPx, maxRatio, maxPx, targetHeight, isTextarea]);

  useLayoutEffect(() => { if (active) refit(); }, [refit, text, active, refitKey]);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!active || !box || typeof ResizeObserver === "undefined") return undefined;
    // Only react to WIDTH changes (height changes are our own fit), and defer to the
    // next frame — refitting synchronously inside the callback resizes the observed box
    // and triggers "ResizeObserver loop completed with undelivered notifications".
    let lastW = box.clientWidth;
    let lastH = box.clientHeight;
    let raf = 0;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      const w = r ? r.width : box.clientWidth;
      const h = r ? r.height : box.clientHeight;
      // Growing boxes (targetHeight): their height is our own doing → width only.
      const changed = Math.abs(w - lastW) >= 0.5 || (!targetHeight && Math.abs(h - lastH) >= 0.5);
      lastW = w; lastH = h;
      if (!changed) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => refit());
    });
    ro.observe(box);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, [boxRef, refit, active, targetHeight, refitKey]);

  return refit;
}
